import React, { memo, useState, useEffect, useRef } from 'react';

/**
 * ImageSwitcher - Simple component that switches between images on an interval
 * Uses a single img element that changes src - no complex positioning
 */
const ImageSwitcher = memo(({
  images = [],
  interval = 500,
  style = {},
  className = '',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const imgRef = useRef(null);

  // Only animate when visible (performance optimization)
  useEffect(() => {
    const element = imgRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Switch images on interval
  useEffect(() => {
    if (!isVisible || images.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, interval);

    return () => clearInterval(timer);
  }, [isVisible, images.length, interval]);

  if (images.length === 0) return null;

  // Simple single image that changes src
  return (
    <img
      ref={imgRef}
      src={images[currentIndex]}
      alt=""
      className={className}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
        ...style,
      }}
    />
  );
});

ImageSwitcher.displayName = 'ImageSwitcher';

export default ImageSwitcher;
